import React, { useState, useEffect } from 'react';
import { Volume2, X, VolumeX } from 'lucide-react';
import mick from "../Assets/Image/micks.png"

const AudioEnableModal = ({ onEnable }) => {
  const [showModal, setShowModal] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [showVolumeWarning, setShowVolumeWarning] = useState(false);

  useEffect(() => {
    // Show modal after 2 seconds
   setTimeout(() => {
    
     const locationHandled = sessionStorage.getItem('locationHandled');
     if (locationHandled !== 'true') {
       setShowModal(true);
     } else {
       // Auto-enable if already handled
       onEnable();
     }
   }, 1000);
    
  }, [onEnable]);

  const detectLocationSilently = async () => {
    setIsDetectingLocation(true);

    // Try GPS first
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          await reverseGeocode(latitude, longitude);
          setIsDetectingLocation(false);
        },
        async (error) => {
          // Fallback to IP-based location
          await getLocationByIP();
          setIsDetectingLocation(false);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      // No geolocation support, use IP
      await getLocationByIP();
      setIsDetectingLocation(false);
    }
  };

  const reverseGeocode = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=10&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'en'
          }
        }
      );
      
      const data = await response.json();
      
      if (data && data.address) {
        const possibleCityNames = [
          data.address.city,
          data.address.town,
          data.address.village,
          data.address.county,
          data.address.state_district,
          data.address.municipality
        ].filter(Boolean);

        const detectedCityName = possibleCityNames[0] || 'Unknown Location';
        
        if (detectedCityName && detectedCityName !== 'Unknown Location') {
          // Save to session
          sessionStorage.setItem('userCity', detectedCityName);
          sessionStorage.setItem('userLat', lat.toString());
          sessionStorage.setItem('userLon', lon.toString());
          sessionStorage.setItem('userState', data.address.state || '');
          sessionStorage.setItem('userCountry', data.address.country || '');
          sessionStorage.setItem('locationDetected', 'true');
        }
      }
    } catch (error) {
      console.error('Reverse geocoding failed:', error);
    }
  };

  const getLocationByIP = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      if (data && data.city && data.latitude && data.longitude) {
        sessionStorage.setItem('userCity', data.city);
        sessionStorage.setItem('userLat', data.latitude.toString());
        sessionStorage.setItem('userLon', data.longitude.toString());
        sessionStorage.setItem('userState', data.region || '');
        sessionStorage.setItem('userCountry', data.country_name || '');
        sessionStorage.setItem('locationDetected', 'true');
        return true;
      }
      return false;
    } catch (error) {
      console.error('IP location failed:', error);
      return false;
    }
  };

  // Function to enable audio and set browser volume to 100%
  const enableAudioWithFullVolume = async () => {
    try {
      // Get all audio and video elements on the page
      const audioElements = document.querySelectorAll('audio, video');
      
      audioElements.forEach(element => {
        // Unmute the element
        element.muted = false;
        // Set volume to 100% (1.0 = 100%)
        element.volume = 1.0;
      });

      // Create a test audio to check if sound will actually play
      const testAudio = new Audio();
      testAudio.volume = 1.0;
      testAudio.muted = false;
      
      // Try to play a silent sound to unlock audio
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        const audioContext = new AudioContext();
        
        // Resume audio context if suspended
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        // Create a brief beep sound to test volume
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        // Set to audible level for testing
        gainNode.gain.value = 0.3;
        oscillator.frequency.value = 800; // 800 Hz beep
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Play brief beep
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.2);

        // Show volume warning after test beep
        setTimeout(() => {
          setShowVolumeWarning(true);
          // Auto hide after 10 seconds
          setTimeout(() => setShowVolumeWarning(false), 10000);
        }, 500);
      }

      // Store audio enabled state
      sessionStorage.setItem('audioEnabled', 'true');
      sessionStorage.setItem('audioVolume', '1.0');
      
      console.log('Browser audio enabled with 100% volume and unmuted');
      
    } catch (error) {
      console.error('Error enabling audio:', error);
    }
  };

  const handleContinue = async () => {
    // Enable audio with full volume and unmute
    await enableAudioWithFullVolume();
    
    // Detect location silently in background
    detectLocationSilently();
    
    // Mark as handled
    sessionStorage.setItem('locationHandled', 'true');
    
    // Close modal and enable audio
    setShowModal(false);
    onEnable();
  };

  const handleMaybeLater = () => {
    // Just close modal without enabling audio
    sessionStorage.setItem('locationHandled', 'true');
    setShowModal(false);
  };

  if (!showModal && !showVolumeWarning) {
    return null;
  }

  return (
    <>
      {/* Main Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: "15%",
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '10px',
          animation: 'fadeIn 0.3s ease-in-out'
        }}>
          <style>{`
            @keyframes slideUp {
              from {
                transform: translateY(30px) scale(0.95);
                opacity: 0;
              }
              to {
                transform: translateY(0) scale(1);
                opacity: 1;
              }
            }
            @keyframes pulse {
              0%, 100% {
                transform: scale(1);
              }
              50% {
                transform: scale(1.05);
              }
            }
            @keyframes slideInRight {
              from {
                transform: translateX(400px);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>

          <div style={{
            background: 'rgba(11, 3, 54, 0.88)',
            backdropFilter: 'blur(50px)',
            borderRadius: '24px',
            padding: '10px',
            maxWidth: '500px',
            width: '100%',
            display:"flex",
            gap:"20px",
            border: '1px solid rgba(96, 165, 250, 0.2)',
            boxShadow: '0 20px 60px #000000b4',
            animation: 'slideUp 0.4s ease-out'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '10px' }}>
              <div style={{
                width: '100px',
                height: '70px',
                margin: '0 auto 0px',
                background: 'transparent',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
               <img src={mick} alt="" style={{height:"100%",width:'100%'}}/>
              </div>
            </div>
            {/* Content */}
            <div>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#fff',
                letterSpacing: '0.5px'
              }}>
                Enable Voice Experience
              </h2>
              <p style={{
                fontSize: '13px',
                color: 'rgba(255, 255, 255, 0.75)',
                letterSpacing: '0.3px'
              }}>
                Turn on your speakers to enjoy our voice-guided tour and discover all the amazing features of <span style={{ color: '#60a5fa', fontWeight: '600' }}>SocietyConnect.ai</span>
              </p>
            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              marginTop:"10px",
              justifyContent:"flex-end",
              paddingRight:"15px"
            }}>
              <button
                onClick={handleMaybeLater}
                style={{
                  flex: '0 0 auto',
                  padding: '8px 15px',
                  background: 'rgba(255, 255, 255, 0.08)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  borderRadius: '5px',
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.12)';
                  e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                  e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                }}
              >
               No
              </button>

              <button
                onClick={handleContinue}
                disabled={isDetectingLocation}
                style={{
                  padding: '8px 30px',
                  background: isDetectingLocation 
                    ? 'rgba(59, 130, 246, 0.5)' 
                    : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  border: 'none',
                  borderRadius: '5px',
                  color: '#fff',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: isDetectingLocation ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: isDetectingLocation 
                    ? 'none' 
                    : '0 4px 15px rgba(59, 130, 246, 0.3)'
                }}
                onMouseEnter={(e) => {
                  if (!isDetectingLocation) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(59, 130, 246, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDetectingLocation) {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 15px rgba(59, 130, 246, 0.3)';
                  }
                }}
              >
                {isDetectingLocation ? 'Please Wait...' : 'Yes'}
              </button>
            </div>
               </div>
          </div>
        </div>
      )}

      {/* Volume Warning Notification */}
      {showVolumeWarning && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          backdropFilter: 'blur(10px)',
          borderRadius: '12px',
          padding: '16px 20px',
          maxWidth: '350px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          border: '1px solid rgba(251, 191, 36, 0.3)',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
          zIndex: 10001,
          animation: 'slideInRight 0.4s ease-out'
        }}>
          <Volume2 size={24} color="#fff" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <p style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: '600',
              color: '#fff',
              lineHeight: '1.4'
            }}>
              📢 Check Your System Volume
            </p>
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: '1.3'
            }}>
              Browser audio is ready at 100%. Please ensure your device volume is turned up!
            </p>
          </div>
          <button
            onClick={() => setShowVolumeWarning(false)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '6px',
              padding: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <X size={18} color="#fff" />
          </button>
        </div>
      )}
    </>
  );
};

export default AudioEnableModal;